import React, { useState, useRef } from "react";
import { FaPlay, FaPause, FaStepForward, FaStepBackward, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import useMethodStore, { useMethodActions } from "@/store/MethodStore";
import useSettingsStore from "@/store/SettingsStore";
import { IoSettings } from "react-icons/io5";
import formatTime from "@/utils/formatTime";
import { useMusicPlayer } from "@/hooks/useMusicPlayer";
import useMusicStore, { useMusicActions } from "@/store/MusicStore";

export type fetchedMusicType = {
  id: string
  name: string;
  artists: {
    primary: [
      { name: string; }
    ]
  };
  downloadUrl: { url: string; }[];
  image: { url: string; }[];
};

const MusicPlayer = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<fetchedMusicType[]>([]); // To store search suggestions
  const [isSongSearching, setIsSongSearching] = useState(false); // Loading state for search

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const smallButtonSize = window.matchMedia("(max-width: 768px)").matches ? 24 : 20
  const largeButtonSize = window.matchMedia("(max-width: 768px)").matches ? 28 : 24

  const { setSettingsState, setFocusedWindow } = useMethodActions()
  const setActiveTab = useSettingsStore(state => state.setActiveTab)
  const { addMusic,setSongIndex, setIsMuted } = useMusicActions()

  const settingsState = useMethodStore(state => state.settingsState)
  const activeTab = useSettingsStore(state => state.activeTab)
  const isMuted = useMusicStore(state => state.isMuted)
  const isSongFetching = useMusicStore(state => state.isSongFetching)
  const musicHistory = useMusicStore(state => state.musicHistory)
  const songIndex = useMusicStore(state => state.songIndex)
  const duration = useMusicStore(state => state.duration)
  const playerRef = useMusicStore(state => state.playerRef)
  const progress = useMusicStore(state => state.progress)
  const isPlaying = useMusicStore(state => state.isPlaying)

  const {
    playPrev,
    playNext,
    handleSeek,
    togglePlayPause,
    getSongToaster,
  } = useMusicPlayer()

  // Fetch search suggestions based on query
  const fetchSearchSuggestions = async (query: string) => {
    setIsSongSearching(true); // Start searching
    try {
      const serverUri = import.meta.env.VITE_SERVER_URI || "";
      const response = await axios.get(
        `${serverUri}/music/search?query=${encodeURIComponent(query)}`
      );

      if (!response.data.data.results) return
      console.log(response.data.data.results)
      setSearchResults(response.data.data.results); // Store the search results
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Error fetching search suggestions")
      console.error("Error fetching search suggestions:", error);
    } finally {
      setIsSongSearching(false); // Stop searching once done
    }
  };

  // Handle search input change with debouncing
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (e.target.value) {
      // Clear the previous debounce timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      // Set a new debounce timeout
      debounceTimeout.current = setTimeout(() => {
        fetchSearchSuggestions(e.target.value); // Call the API after debounce delay
      }, 500); // Debounce delay in milliseconds (e.g., 500ms)
    } else {
      setSearchResults([]);
    }
  };

  // Fetch song from search results
  const handleSearchSelect = (selectedSong: fetchedMusicType) => {
    const songData = {
      id: selectedSong.id,
      title: selectedSong.name,
      artist: selectedSong.artists.primary[0].name,
      url: selectedSong.downloadUrl[1].url,
      cover: selectedSong.image[1].url || "https://via.placeholder.com/150",
    };
    addMusic(songData);
    setSongIndex(songIndex + 1);
    setSearchQuery(""); // Clear search query after selecting a song
    setSearchResults([]); // Clear search suggestions
    if (isPlaying) getSongToaster(songData.cover, songData.title, songData.artist)
  };

  return (
    <div className="relative flex flex-col items-center justify-center p-4 bg-gray-300/95 backdrop-blur-md text-zinc-900 dark:bg-gray-800/95 dark:text-white shadow-md w-full h-full md:h-fit md:w-[350px]">
      {/* Display loading state when searching */}
      {isSongSearching && (
        <div className="absolute top-0 left-0 w-full h-[0.200rem] loading-bar-container overflow-hidden bg-blue-500">
          <div className="loading-bar h-[0.200rem] w-full bg-blue-900"></div>
        </div>
      )}
      <div className="relative mb-4 mt-2 w-full">

        {/* Search input */}
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full px-4 py-2 rounded bg-gray-200 text-zinc-800 dark:bg-gray-700 dark:text-zinc-100"
          placeholder="Search for a song"
        />

        {/* Display search suggestions */}
        {searchQuery && !isSongSearching && searchResults.length > 0 && (
          <div className="absolute top-10 left-0 w-full bg-gray-600 dark:bg-gray-700 rounded mt-2 p-2">
            {searchResults.map((result: fetchedMusicType) => (
              <div
                key={result.id}
                className="p-2 flex items-center space-x-3 text-white hover:bg-gray-700 dark:hover:bg-gray-600 cursor-pointer"
                onClick={() => handleSearchSelect(result)}
              >
                <img
                  src={result.image[1].url}
                  alt={result.name}
                  className="w-10 h-10 rounded-md"
                />
                <div>
                  <p className="line-clamp-1">{result?.name || "Not found"}</p>
                  <p className="text-sm lincm text-zinc-700 dark:text-zinc-300">{result.artists.primary[0]?.name || "Not found"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Show loading state or song details */}
      {!musicHistory[songIndex] ? (
        isSongFetching
          ?   // Skeleton Loader
          <div className="animate-pulse w-full">
            {/* Skeleton for Image */}
            <div className="w-80 md:w-60 h-80 md:h-60 bg-gray-700 rounded-md mx-auto"></div>

            {/* Skeleton for Song Title and Artist */}
            <div className="text-center pt-16 md:pt-6">
              <div className="h-6 bg-gray-700 rounded w-2/3 mx-auto mb-3"></div>
              <div className="h-4 bg-gray-600 rounded w-1/3 mx-auto"></div>
            </div>

            {/* Skeleton for Progress Bar */}
            <div className="w-full px-6 md:px-0 pt-6 md:pt-3">
              <div className="flex items-center justify-between text-sm">
                <div className="h-4 bg-gray-700 rounded w-10"></div>
                <div className="h-4 bg-gray-700 rounded w-10"></div>
              </div>
              <div className="h-2 bg-gray-700 rounded w-full mt-2"></div>
            </div>

            {/* Skeleton for Controls */}
            <div className="flex items-center justify-center space-x-8 md:space-x-6 py-12 md:py-6">
              <div className="w-16 md:w-12 h-16 md:h-12 bg-gray-700 rounded-full"></div>
              <div className="w-16 md:w-12 h-16 md:h-12 bg-gray-700 rounded-full"></div>
              <div className="w-16 md:w-12 h-16 md:h-12 bg-gray-700 rounded-full"></div>
            </div>
          </div>
          : (<p className="text-zinc-900 dark:text-zinc-100 py-24">No song loaded. Search for a song!</p>)
      ) : (
        <>
          {/* Song Information */}
          <img
            src={musicHistory[songIndex].cover}
            alt={musicHistory[songIndex].title}
            className="w-80 md:w-60 h-80 md:h-60 mt-4 rounded-md"
          />
          <div className="text-center pt-16 md:pt-6">
            <h3 className="text-3xl md:text-2xl font-semibold line-clamp-1">{musicHistory[songIndex].title}</h3>
            <p className="text-gray-600 dark:text-gray-400 text-lg md:text-base">{musicHistory[songIndex].artist}</p>
          </div>

          {/* Progress Bar */}
          <div className="w-full px-6 md:px-0 pt-6 md:pt-3">
            <div className="flex items-center justify-between text-sm">
              <span>{formatTime(playerRef && playerRef.current?.currentTime || 0)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={handleSeek}
              className="w-full mt-2"
            />
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-8 md:space-x-6 py-12 md:py-6">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className={`${isMuted ? "text-red-600 dark:text-red-500" : "text-zinc-600 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-500"} transition duration-200`}
            >
              {isMuted ? <FaVolumeMute size={smallButtonSize} /> : <FaVolumeUp size={smallButtonSize} />}
            </button>
            <button
              onClick={playPrev}
              className="text-zinc-900 dark:text-zinc-100 hover:text-blue-600 dark:hover:text-blue-500 transition duration-200"
            >
              <FaStepBackward size={largeButtonSize} />
            </button>
            <button
              onClick={togglePlayPause}
              className="text-zinc-900 dark:text-zinc-100 hover:text-blue-600 dark:hover:text-blue-500 transition duration-200"
            >
              {isPlaying ? <FaPause size={largeButtonSize} /> : <FaPlay size={largeButtonSize} />}
            </button>
            <button
              onClick={playNext}
              className="text-zinc-900 dark:text-zinc-100 hover:text-blue-600 dark:hover:text-blue-500 transition duration-200"
            >
              <FaStepForward size={largeButtonSize} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setActiveTab("music")
                setSettingsState("floating")
                setFocusedWindow("Settings")
              }}
              className={`${(settingsState !== "closed" && activeTab === "music") ? "text-blue-500" : "text-zinc-600 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-500"} transition duration-200`}
            >
              <IoSettings size={smallButtonSize} />
            </button>
          </div>
        </>
      )}

    </div>
  );
};

export default MusicPlayer;